import { Box, useTheme } from '@mui/material';
import { TrackerDataType } from '../../shared/constants/constants';
import { CalendarEvent } from './types';
import { calendarUiPalette, getCalendarEventPillPalette } from './calendarPalette';

interface CalendarEventPillProps {
  event: CalendarEvent;
  muted?: boolean;
  onClick: (event: CalendarEvent) => void;
  onDoubleClick: (event: CalendarEvent) => void;
}

export default function CalendarEventPill({
  event,
  muted = false,
  onClick,
  onDoubleClick,
}: Readonly<CalendarEventPillProps>) {
  const theme = useTheme();
  const calendarEventPillPalette = getCalendarEventPillPalette(theme);
  const palette = calendarEventPillPalette[event.logtype] ?? calendarEventPillPalette[TrackerDataType.SERVICE];

  return (
    <Box
      component="button"
      type="button"
      title={event.primaryTitle}
      data-cy={`calendar-event-pill-${event.logtype}-${event.id}`}
      onClick={(clickEvent) => {
        clickEvent.stopPropagation();
        onClick(event);
      }}
      onDoubleClick={(clickEvent) => {
        clickEvent.stopPropagation();
        onDoubleClick(event);
      }}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        width: '100%',
        overflow: 'hidden',
        p: 0,
        border: 0,
        borderRadius: '7px',
        backgroundColor: muted ? palette.mutedBackgroundColor : palette.backgroundColor,
        textAlign: 'left',
        cursor: 'pointer',
        appearance: 'none',
        transition: 'transform 120ms ease',
        '&:hover': {
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Box
        component="span"
        sx={{
          minHeight: '100%',
          width: '4px',
          flexShrink: 0,
          borderRadius: '7px 0 0 7px',
          backgroundColor: muted ? palette.mutedBorderColor : palette.borderColor,
        }}
      />
      <Box
        component="span"
        sx={{
          minWidth: 0,
          flex: 1,
          overflow: 'hidden',
          px: 1.25,
          py: 0.75,
          color: calendarUiPalette.eventModal.primaryText,
          fontFamily: theme.typography.body1.fontFamily,
          fontSize: '0.75rem',
          fontWeight: theme.typography.fontWeightMedium,
          lineHeight: 1.2,
          letterSpacing: 'normal',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {event.primaryTitle}
      </Box>
    </Box>
  );
}
